/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: StructuredLanguageEditor
 * Reason: Replaces the script editor for LogicLanguage.mapping — picks the lg.LogicMappingTable behind the typed FK Logic.mappingTable, shows the table's fallback configuration and the immutable parameter contract of the language.
 */
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {LogicDto} from '../../../model/dto/entity/logic.dto';
import {LogicMappingTableDto} from '../../../model/dto/entity/logic-mapping-table.dto';
import {LogicMappingTableService} from '../../../service/api/logic-mapping-table.service';
import {LogicService} from '../../../service/api/logic.service';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {MvsCrudService} from '../../../../../core/shared/service/crud/mvs-crud.service';
import {ObjectRequestList} from '../../../../../core/shared/dto/object/request/object-request-list';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {PagingDto} from '../../../../../core/shared/dto/model/paging.dto';
import {DtoList} from '../../../../../core/shared/dto/dto.list';
import {DtoDetail} from '../../../../../core/shared/dto/dto.detail';
import {getMappingFallbackLabel, MappingFallbackEnum} from '../../../enum/mapping-fallback.enum';

/**
 * One row of the immutable parameter contract shown below the picker.
 */
interface MappingContractRow {
    alias: string;
    type: string;
    mandatory: boolean;
    description: string;
}

/**
 * Option shown in the mapping-table picker.
 */
interface MappingTableOption {
    label: string;
    value: number;
}

/**
 * Configuration surface of a logic written in `LogicLanguage.mapping`.
 *
 * A mapping logic has NO script — its entire configuration is the typed foreign key
 * `Logic.mappingTable`. This component therefore replaces the CodeMirror editor rather than sitting
 * next to it, the same way the format / module-function / crud editors do for their languages.
 *
 * It writes the FK itself (a small PUT carrying only `id` and `mappingTableDtoId`, matching what
 * `updateScript()` on the logic OBC already does) and emits {@link mappingTableChange} so the host
 * can refresh anything derived from the table.
 */
@Component({
    selector: 'mvs-lg-mapping-table-select',
    templateUrl: './lg-mapping-table-select.component.html',
    styleUrls: ['./lg-mapping-table-select.component.scss'],
    standalone: false
})
export class LgMappingTableSelectComponent implements OnInit, OnChanges {

    /** The logic being configured; `mappingTableDtoId` is read and written. */
    @Input() logicDto: LogicDto;

    /** Emitted with the newly persisted mapping table id (or null when it was cleared). */
    @Output() mappingTableChange: EventEmitter<number> = new EventEmitter<number>();

    initialized: boolean = false;
    busy: boolean = false;
    saving: boolean = false;
    countsBusy: boolean = false;

    /** Technical failure while loading the tables — distinct from 'there are none'. */
    loadErrorMessage: string | null = null;

    mappingTableOptions: MappingTableOption[] = [];
    mappingTables: LogicMappingTableDto[] = [];

    selectedMappingTableId: number | null = null;
    selectedMappingTable: LogicMappingTableDto | null = null;

    entryCount: number | null = null;
    overrideCount: number | null = null;

    private entryService: MvsCrudService;
    private overrideService: MvsCrudService;

    /**
     * The three import parameters of the language. Displayed read-only: they are provisioned by the
     * backend `OnObjectChange` handler as real `lg.LogicImport` rows, so this is documentation of what
     * already exists, never an editor.
     */
    readonly contractImports: MappingContractRow[] = [
        {
            alias: 'inputValue',
            type: 'Text',
            mandatory: true,
            description: 'Der aufzulösende Quellwert.'
        },
        {
            alias: 'defaultValueOnNothing',
            type: 'Text',
            mandatory: false,
            description: 'Vorgabewert des Aufrufers, falls nichts gefunden wird. Ein leerer Text gilt als übergeben — nur das Weglassen bedeutet „kein Vorgabewert“.'
        },
        {
            alias: 'partnerAlias',
            type: 'Text',
            mandatory: false,
            description: 'Partner für die Override-Auflösung. Übersteuert den Partner aus dem Ausführungskontext.'
        }
    ];

    /** The four named exports of the language. */
    readonly contractExports: MappingContractRow[] = [
        {
            alias: 'outputValue',
            type: 'Text',
            mandatory: false,
            description: 'Der aufgelöste Wert.'
        },
        {
            alias: 'mappingFound',
            type: 'Ja/Nein',
            mandatory: false,
            description: 'Nur bei Treffer auf einen aktiven Eintrag oder einen Partner-Override wahr — nicht bei einem Fallback.'
        },
        {
            alias: 'defaultValueUsed',
            type: 'Ja/Nein',
            mandatory: false,
            description: 'Nur wahr, wenn der Vorgabewert des Aufrufers ausgegeben wurde. Der Fallback-Wert der Tabelle setzt es nicht.'
        },
        {
            alias: 'resolutionSource',
            type: 'Text',
            mandatory: false,
            description: 'Woher der Wert stammt. Immer gesetzt — auch dann, wenn es bewusst keinen Wert gibt.'
        }
    ];

    /**
     * The signed contract limit from the requirement (§4.7 gap 1), shown verbatim in the editor.
     *
     * Without it, a caller that never sees `outputValue` has no way to tell 'deliberately no value'
     * from 'something went wrong'.
     */
    readonly absentOutputNote: string =
        'Wird kein Wert gefunden und kein Vorgabewert übergeben, entfällt die Ausgabe „outputValue“ ' +
        'vollständig. „resolutionSource“ zeigt dann „Kein Wert (Fallback: leer)“ — daran, und nur ' +
        'daran, ist die Abwesenheit als beabsichtigt erkennbar. Ein leerer Text wird bewusst NICHT ' +
        'ersatzweise geliefert, weil er sonst mit einem Treffer auf einen leeren Zielwert verwechselbar wäre.';

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected logicService: LogicService,
        protected mappingTableService: LogicMappingTableService) {
    }

    ngOnInit(): void {
        this.entryService = this.coreService.getCrudService('lg.LogicMappingTableEntry');
        this.overrideService = this.coreService.getCrudService('lg.LogicMappingTableOverride');

        this.selectedMappingTableId = this.logicDto?.mappingTableDtoId ?? null;
        this.loadMappingTables();

        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['logicDto']) {
            this.selectedMappingTableId = this.logicDto?.mappingTableDtoId ?? null;
            // The assignment decides which deactivated table (if any) the picker still offers.
            this.buildMappingTableOptions();
            this.applySelection();
        }
    }

    /**
     * Loads every mapping table for the picker.
     *
     * Kept unfiltered on purpose: a logic may legitimately point at a table from any logic group, and
     * hiding candidates would look like 'the table does not exist'.
     */
    loadMappingTables(): void {
        this.busy = true;
        this.loadErrorMessage = null;

        const request: ObjectRequestList = ObjectRequestList.createBasic(
            false,
            [],
            [new Sorting('name', true)]
        );

        this.mappingTableService.list(request).subscribe({
            next: (result: DtoList) => {
                // Everything is kept, including deactivated tables: the currently assigned one has to
                // stay resolvable for display even when it is deactivated. Only the OPTIONS are
                // filtered — see buildMappingTableOptions().
                this.mappingTables = (result?.entries ?? []) as LogicMappingTableDto[];
                this.buildMappingTableOptions();
                this.applySelection();
                this.busy = false;
            },
            error: () => {
                this.mappingTables = [];
                this.mappingTableOptions = [];
                this.loadErrorMessage = 'Die Mapping-Tabellen konnten nicht geladen werden.';
                this.busy = false;
            }
        });
    }

    /**
     * Persists the picked table on the logic.
     *
     * The PUT carries only the id and the FK — `Logic` extends `AuditableEntity` and has none of the
     * typed visual fields whose partial updates are known to null each other out, so a narrow payload
     * is safe here and matches the existing `updateScript()` call on the same entity.
     */
    onMappingTableSelected(): void {
        if (!this.logicDto?.id) {
            return;
        }

        this.applySelection();
        this.saving = true;

        const update: LogicDto = new LogicDto();
        update.id = this.logicDto.id;
        update.mappingTableDtoId = this.selectedMappingTableId;

        this.logicService.update(update).subscribe({
            next: (res: DtoDetail) => {
                this.logicDto.mappingTableDtoId = this.selectedMappingTableId;
                this.logicDto.mappingTableDtoName = (res as LogicDto)?.mappingTableDtoName
                    ?? this.selectedMappingTable?.name;
                // Only now, with the new assignment actually persisted, may a deactivated table that
                // was the previous assignment drop out of the list. Rebuilding before the save would
                // remove it while it is still the one in effect.
                this.buildMappingTableOptions();
                this.saving = false;
                this.messageService.showSuccessMessage(
                    'Mapping-Tabelle',
                    'Die Zuordnung wurde gespeichert.'
                );
                this.mappingTableChange.emit(this.selectedMappingTableId);
            },
            error: () => {
                this.saving = false;
                this.messageService.showErrorMessage(
                    'Mapping-Tabelle',
                    'Die Zuordnung konnte nicht gespeichert werden.'
                );
            }
        });
    }

    /** Label for the picker: name plus alias, because aliases are what scripts and Java calls use. */
    buildOptionLabel(table: LogicMappingTableDto): string {
        if (!table) {
            return '';
        }
        return table.alias ? `${table.name} (${table.alias})` : table.name;
    }

    /**
     * Whether a table may be chosen for a NEW assignment.
     *
     * `null` and `undefined` mean ACTIVE — the column was added to tables that already held
     * productive rows, so every pre-existing row sits at `NULL`. Testing `active === true` here would
     * empty the picker on every existing tenant.
     *
     * @param table the table to judge
     */
    isTableActive(table: LogicMappingTableDto): boolean {
        return table?.active !== false;
    }

    /**
     * True when the logic is assigned to a table that has since been deactivated.
     *
     * The assignment keeps working — `active` governs selection, not resolution — so the UI has to
     * keep showing it. It is the one deactivated table the picker still offers.
     */
    isSelectedTableInactive(): boolean {
        return !!this.selectedMappingTable && !this.isTableActive(this.selectedMappingTable);
    }

    /**
     * Rebuilds the picker options: active tables, plus the ASSIGNED one even when deactivated.
     *
     * Dropping a deactivated table that a logic still points at would leave the user staring at an
     * empty field while the logic goes on resolving against that very table — a display that lies.
     * So it stays, marked, and disappears from the list as soon as the assignment has actually moved
     * on.
     *
     * The exception is keyed on the PERSISTED assignment (`logicDto.mappingTableDtoId`), not on the
     * transient selection: if a save fails, the logic still points at the deactivated table and the
     * option must still be there.
     */
    protected buildMappingTableOptions(): void {
        const assignedId: number = this.logicDto?.mappingTableDtoId ?? null;

        this.mappingTableOptions = this.mappingTables
            .filter(table => this.isTableActive(table) || table.id === assignedId)
            .map(table => ({
                label: this.isTableActive(table)
                    ? this.buildOptionLabel(table)
                    : `${this.buildOptionLabel(table)} (deaktiviert)`,
                value: table.id
            }));
    }

    /** German label of the selected table's fallback strategy, including the 'unset' case. */
    getFallbackLabel(): string {
        return getMappingFallbackLabel(this.selectedMappingTable?.fallbackBehaviour);
    }

    /** True when the table falls back to its own default value, so the value itself matters. */
    showsFallbackValue(): boolean {
        return this.selectedMappingTable?.fallbackBehaviour === MappingFallbackEnum.DEFAULT_VALUE;
    }

    /** True when the table has no fallback strategy at all — which behaves like a hard error. */
    hasUnsetFallback(): boolean {
        return this.selectedMappingTable !== null
            && (this.selectedMappingTable?.fallbackBehaviour === null
                || this.selectedMappingTable?.fallbackBehaviour === undefined);
    }

    /** True while the mandatory FK is missing — the one thing this editor validates in the UI. */
    isMappingTableMissing(): boolean {
        return !this.selectedMappingTableId;
    }

    /** Resolves the picked id to its DTO and refreshes the size figures. */
    protected applySelection(): void {
        this.selectedMappingTable = this.mappingTables
            .find(table => table.id === this.selectedMappingTableId) ?? null;

        this.entryCount = null;
        this.overrideCount = null;

        if (this.selectedMappingTableId) {
            this.loadCounts(this.selectedMappingTableId);
        }
    }

    /**
     * Loads how many entries and partner overrides the table holds.
     *
     * Asks for a single row and reads the total off the paging response instead of pulling the whole
     * table — a mapping table can hold five figures worth of entries and the figure here is only a
     * size hint.
     *
     * The filter names the RELATION, `mappingTable`, not `mappingTableDtoId`. This is a generic
     * `/list` request, and `FilterService.toPredicate` resolves the field straight against the JPA
     * root (`root.get(field)`, `core/filter/FilterService.java:281`): no `Dto` normalisation, no dot
     * traversal, no `Id`-suffix stripping. A name that is not a JPA attribute throws, so both
     * subscriptions would land in their error branch and the size line would read `? Einträge` for
     * ever. The relation form works because the same method compares a relation attribute on
     * `path.get("id")`. This is the OPPOSITE convention from QL, where `e.mappingTableId` is right.
     */
    protected loadCounts(mappingTableId: number): void {
        if (!this.entryService || !this.overrideService) {
            return;
        }

        this.countsBusy = true;

        // Both requests are in flight at once; the flag clears when the second one lands, whether it
        // succeeded or not. Clearing it in only one branch leaves the skeleton up for ever if the
        // other is the slow one.
        let pending: number = 2;
        const settle = () => {
            pending--;
            if (pending <= 0) {
                this.countsBusy = false;
            }
        };

        const request = () => ObjectRequestList.createWithPaging(
            false,
            [FilterCriteria.create('mappingTable', FilterCriteria.cOperatorEqual, mappingTableId)],
            [],
            PagingDto.createSize(1)
        );

        this.entryService.list(request()).subscribe({
            next: (result: DtoList) => {
                this.entryCount = result?.pagingResponse?.count ?? result?.entries?.length ?? 0;
                settle();
            },
            error: () => {
                this.entryCount = null;
                settle();
            }
        });

        this.overrideService.list(request()).subscribe({
            next: (result: DtoList) => {
                this.overrideCount = result?.pagingResponse?.count ?? result?.entries?.length ?? 0;
                settle();
            },
            error: () => {
                this.overrideCount = null;
                settle();
            }
        });
    }
}
